import { motion, useReducedMotion } from "framer-motion";
import { Phone } from "lucide-react";
import { useLocation } from "react-router-dom";
import { SITE } from "../data/site";
import { useLang } from "../i18n";
import { getTempo } from "../utils/tempo";
import { routeKey } from "../utils/perf";

// The Call Now button shifts personality per page.
// Per-page accent identity — orange/blue alternating (owner's call), matched
// to the route accent vars in App.js.
const FAB_THEMES = {
  "/": { bg: "#C75B12", fg: "#EDF1F7", ring: "rgba(240,146,62,0.55)", glow: "rgba(240,146,62,0.55)" },
  "/repair-lab": { bg: "#2E6BE6", fg: "#EDF1F7", ring: "rgba(107,155,255,0.55)", glow: "rgba(107,155,255,0.55)" },
  "/phones": { bg: "#C75B12", fg: "#EDF1F7", ring: "rgba(240,146,62,0.55)", glow: "rgba(240,146,62,0.55)" },
  "/showroom": { bg: "#2E6BE6", fg: "#EDF1F7", ring: "rgba(107,155,255,0.55)", glow: "rgba(107,155,255,0.55)" },
  "/visit": { bg: "#C75B12", fg: "#EDF1F7", ring: "rgba(240,146,62,0.55)", glow: "rgba(240,146,62,0.55)" },
  "/quote": { bg: "#2E6BE6", fg: "#EDF1F7", ring: "rgba(107,155,255,0.55)", glow: "rgba(107,155,255,0.55)" },
};
const DEFAULT_THEME = FAB_THEMES["/"];

export const FloatingCall = () => {
  const { t } = useLang();
  const reduce = useReducedMotion();
  const { pathname } = useLocation();
  const theme = FAB_THEMES[routeKey(pathname)] || DEFAULT_THEME;
  const T = getTempo();

  return (
    <motion.a
      href={SITE.phoneHref}
      data-testid="floating-call-button"
      aria-label={`${t("quote.callNow")} ${SITE.phoneDisplay}`}
      initial={
        reduce
          ? { backgroundColor: theme.bg, color: theme.fg }
          : { scale: 0, y: 24, backgroundColor: theme.bg, color: theme.fg }
      }
      animate={{
        scale: 1,
        y: 0,
        backgroundColor: theme.bg,
        color: theme.fg,
        boxShadow: `0 18px 45px -10px ${theme.glow}`,
      }}
      transition={{
        delay: 1.1,
        type: "spring",
        stiffness: 220,
        damping: 21,
        backgroundColor: { duration: 1.05, ease: "easeInOut" },
        boxShadow: { duration: 1.05, ease: "easeInOut" },
      }}
      className="group fixed bottom-5 end-5 z-50 flex items-center gap-2.5 rounded-full py-3.5 pe-6 ps-4 font-bold uppercase tracking-wide sm:bottom-7 sm:end-7"
    >
      {!reduce && (
        <>
          <motion.span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border-2"
            initial={{ scale: 1, opacity: 0, borderColor: theme.ring }}
            animate={{ scale: [1, 1.55], opacity: [0.6, 0], borderColor: theme.ring }}
            transition={{ duration: 2.1 * T, repeat: Infinity, ease: "easeOut", borderColor: { duration: 0.9 } }}
          />
          <motion.span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border-2"
            initial={{ scale: 1, opacity: 0, borderColor: theme.ring }}
            animate={{ scale: [1, 1.55], opacity: [0.6, 0], borderColor: theme.ring }}
            transition={{ duration: 2.1 * T, delay: 1.05 * T, repeat: Infinity, ease: "easeOut", borderColor: { duration: 0.9 } }}
          />
        </>
      )}
      <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-black/15">
        <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
      </span>
      <span className="relative text-sm sm:text-base">{t("quote.callNow")}</span>
    </motion.a>
  );
};
